import { z } from 'zod';
import { availabilitySlotSchema } from '../../ai-platform/availability-platform.js';
import { AiAdvanceValidationError } from '../../ai-platform/ports.js';
import { conversationModeSchema, conversationRuntimeLayerSchema } from './conversation-mode.js';
import { crmFallbackPublicViewSchema } from './crm-fallback.js';
import { athenaRecommendationItemSchema, athenaSessionMemorySchema } from './intelligence.js';

export const athenaInlineUiSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('services'),
      items: z.array(athenaRecommendationItemSchema).min(1).max(3),
    })
    .strict(),
  z
    .object({
      type: z.literal('slots'),
      slots: z.array(availabilitySlotSchema).min(1).max(8),
      staffDisplayName: z.string().max(120).optional(),
    })
    .strict(),
]);

export type AthenaInlineUi = z.infer<typeof athenaInlineUiSchema>;

export const CDLIT_AI_MESSAGE_TEXT_MAX = 1500;

export const cdlitAiRecentTurnSchema = z
  .object({
    role: z.enum(['user', 'assistant']),
    content: z.string().min(1).max(2000),
  })
  .strict();

export type CdlitAiRecentTurn = z.infer<typeof cdlitAiRecentTurnSchema>;

/** Public conversation write — sole discovery endpoint (ATHENA.1). */
export const cdlitAiMessageRequestSchema = z
  .object({
    text: z.string().min(1).max(CDLIT_AI_MESSAGE_TEXT_MAX),
    /** Semantic id of adaptive button; text is still required (chip title). */
    replyId: z.string().min(1).max(64).optional(),
    /** Client idempotency key (ATHENA.3). Same key + payload → same response; no double side effects. */
    requestId: z.string().min(8).max(64).optional(),
    /** Ephemeral prior turns for LLM inference only — not in idempotency hash or FSM. */
    recentTurns: z.array(cdlitAiRecentTurnSchema).max(12).optional(),
  })
  .strict();

export type CdlitAiMessageRequest = z.infer<typeof cdlitAiMessageRequestSchema>;

/**
 * Deterministic conversation actions exposed to the client.
 * Internal intelligence actions map into this set; booking phases use booking_* .
 */
export const athenaConversationActionSchema = z.enum([
  'clarify',
  'recommend',
  'present_alternatives',
  'answer',
  'safety_check',
  'offer_booking',
  'show_slots',
  'booking_contact',
  'booking_confirm',
  'handoff',
  'unavailable',
]);

export type AthenaConversationAction = z.infer<typeof athenaConversationActionSchema>;

export const athenaAdaptiveReplySchema = z
  .object({
    replyId: z.string().min(1).max(64),
    title: z.string().min(1).max(120),
    /** UI emphasis hint — not an FSM step. */
    emphasis: z.enum(['primary', 'secondary', 'ghost']).optional(),
  })
  .strict();

export type AthenaAdaptiveReply = z.infer<typeof athenaAdaptiveReplySchema>;

/** Pending guarded write — confirm via text consent or `replyId` chip. */
export const athenaPendingConfirmSchema = z
  .object({
    confirmId: z.string().min(1).max(64),
    preview: z
      .object({
        tool: z.string().min(1).max(64),
        summary: z.string().min(1).max(500),
      })
      .strict(),
    expiresAt: z.string().datetime(),
  })
  .strict();

export type AthenaPendingConfirm = z.infer<typeof athenaPendingConfirmSchema>;

/** Discovery-facing conversation facts — no legacy FSM presentation. */
export const athenaConversationStateSchema = z
  .object({
    mode: z.literal('intelligence'),
    bookingStage: z
      .enum([
        'discovery',
        'clarify',
        'recommendation',
        'safety_gate',
        'safety_ask',
        'safety_details',
        'offer',
        'slots',
        'contact',
        'confirm',
      ])
      .optional(),
    direction: athenaSessionMemorySchema.shape.direction,
    goal: athenaSessionMemorySchema.shape.goal,
    concerns: z.array(z.string()).default([]),
    clientStatus: athenaSessionMemorySchema.shape.client_status,
    knownServiceHint: z.string().max(120).optional(),
    readinessToBook: z.boolean().optional(),
    updatedAt: z.string().datetime().optional(),
    revision: z.number().int().nonnegative().optional(),
    /** Structural safety outcome for UI — not free-text medical advice. */
    safetyNote: athenaSessionMemorySchema.shape.safety_note,
    safetyRoute: athenaSessionMemorySchema.shape.safety_route,
  })
  .strict();

export type AthenaConversationState = z.infer<typeof athenaConversationStateSchema>;

export const athenaBookingPhaseSchema = z.enum([
  'slots',
  'contact',
  'confirm',
  'pending',
  'completed',
  'conflict',
  'unavailable',
]);

export type AthenaBookingPhase = z.infer<typeof athenaBookingPhaseSchema>;

export const cdlitAiConversationResponseSchema = z
  .object({
    sessionId: z.string().min(1),
    reply: z.string().min(1).max(500),
    action: athenaConversationActionSchema,
    source: z.enum(['ai', 'fallback', 'template']),
    /**
     * Server runtime mode (`ai` | `crm_fallback`). Distinct from `conversation.mode` (intelligence bridge).
     * Required on successful public responses — clients must not default unknown values to `ai`.
     */
    mode: conversationModeSchema,
    /**
     * Кто отвечает: ассистент, сценарный бот или офлайн-минимум.
     *
     * Опционально ради сборок, выпущенных до Ф7: старый виджет поле просто не
     * читает. Новый не гадает по косвенным признакам — см.
     * `conversationRuntimeLayerSchema`.
     */
    runtimeLayer: conversationRuntimeLayerSchema.optional(),
    recommendations: z.array(athenaRecommendationItemSchema).max(3).optional(),
    inlineUi: athenaInlineUiSchema.optional(),
    adaptiveReplies: z.array(athenaAdaptiveReplySchema).max(8),
    conversation: athenaConversationStateSchema,
    /** Present only when client entered technical booking flow. */
    booking: z
      .object({
        phase: athenaBookingPhaseSchema,
        recommendedServiceId: z.string().max(32).optional(),
      })
      .strict()
      .optional(),
    /**
     * Present only when `mode === "crm_fallback"`.
     * Discriminated from AI adaptive discovery — wizard options live here, not in adaptiveReplies.
     */
    crmFallback: crmFallbackPublicViewSchema.optional(),
    /**
     * Bridge for existing booking UI (availability / contact / confirm) that still reads
     * AiPublicSessionView. Discovery must not depend on presentation.options — use adaptiveReplies.
     */
    session: z.unknown().optional(),
    /** Present when agent awaits guarded write confirmation (service-assistant action-guard). */
    pendingConfirm: athenaPendingConfirmSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    // Слой и вид сессии обязаны сходиться. Иначе поля разошлись бы молча:
    // виджет показал бы контакты офлайна, а мастер записи ждал бы кнопок.
    if (value.runtimeLayer != null) {
      const expectedMode = value.runtimeLayer === 'scripted' ? 'crm_fallback' : 'ai';
      if (value.mode !== expectedMode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['runtimeLayer'],
          message: `runtimeLayer "${value.runtimeLayer}" requires mode "${expectedMode}"`,
        });
      }
    }
    if (value.mode === 'crm_fallback' && value.crmFallback == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'crmFallback is required when mode is crm_fallback',
        path: ['crmFallback'],
      });
    }
    if (value.mode === 'ai' && value.crmFallback != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'crmFallback must not be present when mode is ai',
        path: ['crmFallback'],
      });
    }
  });

export type CdlitAiConversationResponse = z.infer<typeof cdlitAiConversationResponseSchema>;

export const CDLIT_AI_MESSAGE_FORBIDDEN_BODY_FIELDS = [
  'productKey',
  'tenantKey',
  'roleKey',
  'systemPrompt',
  'serviceId',
  'staffId',
  'price',
  'slot',
  'action',
  'payload',
  'transcript',
  'kind',
  'assistOptionId',
  'optionId',
] as const;

export function assertNoForbiddenMessageBodyFields(body: unknown): void {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) return;
  for (const key of CDLIT_AI_MESSAGE_FORBIDDEN_BODY_FIELDS) {
    if (key in body) {
      throw new AiAdvanceValidationError(`Field ${key} is not allowed`);
    }
  }
}
